// Shell-Bar-Inhalte der Kalender-Route — W4-Konvergenz: der Kalender
// rendert kein eigenes Mobile-Chrome mehr (mobile-calendar-bar.tsx
// und calendar-bottom-bar.tsx sind hierin aufgegangen), sondern portalt wie
// jede andere Seite in die generischen Shell-Bars (components/shell/*):
//
//   - CalendarMobileTopCenter → Top-Mitte (NUR Phone — das Tablet-Band hat
//     keine Top-Leiste, dort navigiert der Desktop-Kopf mit ◀▶): ◀ ·
//     tappbarer Titel mit Soll/Ist-Ampelpunkt● (öffnet das Zeitraum-Sheet
//     mit Heute + Soll/Ist-Detail) · ▶. 🔥 links und 🔔 rechts liefert
//     MobileShellTop.
//   - CalendarMobileSlots → Bottom-Slots (Phone UND Tablet-Band 640–1023,
//     T3-Adoption; das Gate sitzt in calendar.tsx via useShellBars):
//     Liste · Ansicht(Sheet) · [👤 View-As] · [Stoppuhr live] · ＋
//     Erfassen. Im Band existieren die Aktionen bewusst doppelt (Desktop-
//     Toolbar + Bar — Daumen-Redundanz, identische Handler). Das ☰ liefert
//     MobileShellBottom als fixen Slot 1; suppressBottomBar der Shell
//     wirkt automatisch.
//
// Die Sheets (Zeitraum/Ansicht/View-As/Timer) rendern bewusst NEBEN den
// Portalen (nicht darin): SidePanel/Popover sind selbst fixed/portal-
// verankert und sollen offen bleiben, auch wenn die Bar (suppressBottomBar)
// gerade unmountet ist.
//
// Die Timer-Logik wird NICHT dupliziert: `useRunningEntry` + der komplette
// Sheet-Inhalt (`CalendarTimerPopover`) kommen aus calendar-timer.tsx. Hier
// lebt nur der Slot-Trigger (idle: Icon+„Stoppuhr", laufend: Ping-Dot +
// live tickende Zeit).

import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  PanelLeftOpen,
  Timer as TimerIcon,
  User,
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { invalidateEntryData } from '../../lib/invalidate'
import { type SollIstStatus, sollIstStatus } from '../../lib/soll-ist-status'
import { cn, formatClock } from '../../lib/utils'
import type { ViewMode } from '../../routes/calendar'
import { CalendarTimerPopover, useRunningEntry } from '../calendar-timer'
import { MobileBottomSlots, MobileTopCenter } from '../shell/mobile-shell-outlets'
import { Button } from '../ui/button'
import { BarAccentPlus, BarSlot, SheetRow } from '../ui/mobile-action-bar'
import { SidePanel } from '../ui/side-panel'

// Ampelpunkt-Farben aus den Design-Tokens.
const DOT_CLASS: Record<SollIstStatus, string> = {
  neutral: 'bg-text-tertiary',
  ok: 'bg-success',
  warn: 'bg-warning',
  alert: 'bg-danger',
}

interface CalendarMobileTopCenterProps {
  /** Datums-/Zeitraum-Titel (identisch zum Desktop-Kopf). */
  title: string
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  // ── Soll/Ist-Ampelpunkt ──
  actualMin: number
  targetMin: number
  hasArbzgWarning: boolean
  /** Non-compact SollIstStrip fürs Zeitraum-Sheet (Zahl bleibt erreichbar). */
  sollIstDetail: ReactNode
}

export function CalendarMobileTopCenter({
  title,
  onPrev,
  onNext,
  onToday,
  actualMin,
  targetMin,
  hasArbzgWarning,
  sollIstDetail,
}: CalendarMobileTopCenterProps) {
  const [rangeSheet, setRangeSheet] = useState(false)

  const status = sollIstStatus(actualMin, targetMin, hasArbzgWarning)

  return (
    <>
      {/* ── Top-Mitte: ◀ · Titel● · ▶ (Muster team-planning/disposition) ── */}
      <MobileTopCenter>
        <button
          type="button"
          onClick={onPrev}
          aria-label="Zurück"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-overlay hover:text-text-primary"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        {/* Tappbarer Titel → Zeitraum-Sheet. no-min-tap erlaubt Schrumpfen
            unter 44px auf sehr schmalen Geräten (Overflow-Schutz); h-11 hält
            die 44px-Tap-Höhe. Inline minWidth:0 schlägt die globale
            .no-min-tap-Regel (index.css, @media hover:none), die sonst
            min-width auf auto = min-content setzt → der lange Wochentitel
            würde die Leiste sprengen und die Glocke rausdrücken (P3/P2). */}
        <button
          type="button"
          onClick={() => setRangeSheet(true)}
          aria-label={`Zeitraum: ${title}`}
          style={{ minWidth: 0 }}
          className="no-min-tap flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-1 text-text-primary hover:bg-bg-overlay"
        >
          <span className="truncate text-xs font-medium">{title}</span>
          <span
            className={cn('inline-block h-2 w-2 shrink-0 rounded-full', DOT_CLASS[status])}
            aria-hidden="true"
          />
        </button>

        <button
          type="button"
          onClick={onNext}
          aria-label="Vor"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-overlay hover:text-text-primary"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </MobileTopCenter>

      {/* ── Zeitraum-Sheet: Heute + volle Spanne + Soll/Ist-Zahl ── */}
      <SidePanel open={rangeSheet} onClose={() => setRangeSheet(false)} title={title}>
        <div className="space-y-4">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              onToday()
              setRangeSheet(false)
            }}
          >
            Heute
          </Button>
          {sollIstDetail}
        </div>
      </SidePanel>
    </>
  )
}

interface CalendarMobileSlotsProps {
  view: ViewMode
  onViewChange: (v: ViewMode) => void
  // ── Liste (Info-Panel) ──
  infoPanelOpen: boolean
  onToggleInfoPanel: () => void
  // ── Admin: View-As ──
  showViewAsPicker: boolean
  manageableUsers: { id: string; name: string }[]
  viewAsUserId: string
  onViewAsChange: (id: string) => void
  selfUserId: string
  // ── Aktionen ──
  /** Öffnet das Erfassen-Panel (identisch zum „+" der Desktop-Toolbar). */
  onErfassen: () => void
  /** Lesemodus (fremde Ansicht ohne Bearbeiten) → „＋" deaktiviert. */
  readOnly: boolean
  /** Fremde Ansicht → keine eigene Stoppuhr (Parität zur Desktop-Toolbar). */
  isViewingOther: boolean
}

const VIEW_LABEL: Record<ViewMode, string> = {
  day: 'Tag',
  week: 'Woche',
  month: 'Monat',
  year: 'Jahr',
}

const VIEW_ORDER: ViewMode[] = ['day', 'week', 'month', 'year']

export function CalendarMobileSlots({
  view,
  onViewChange,
  infoPanelOpen,
  onToggleInfoPanel,
  showViewAsPicker,
  manageableUsers,
  viewAsUserId,
  onViewAsChange,
  selfUserId,
  onErfassen,
  readOnly,
  isViewingOther,
}: CalendarMobileSlotsProps) {
  const queryClient = useQueryClient()
  const running = useRunningEntry()
  const [now, setNow] = useState(() => Date.now())
  const [timerOpen, setTimerOpen] = useState(false)
  const [viewSheet, setViewSheet] = useState(false)
  const [viewAsSheet, setViewAsSheet] = useState(false)

  // 1s-Tick nur, solange ein Timer läuft (für die Live-Zeit im Slot).
  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [running])

  const showTimer = !isViewingOther
  const elapsed = running ? now - new Date(running.startTime).getTime() : 0
  const timerRunning = showTimer && !!running

  function onChanged() {
    invalidateEntryData(queryClient)
    queryClient.invalidateQueries({ queryKey: ['entries', 'running'] })
  }

  return (
    <>
      <MobileBottomSlots>
        {/* ── Liste: Info-Panel-Toggle (Anträge/Einträge/Meetings) ── */}
        <BarSlot
          label="Liste"
          icon={<PanelLeftOpen className="h-5 w-5" />}
          active={infoPanelOpen}
          ariaPressed={infoPanelOpen}
          ariaLabel="Anträge / Einträge / Meetings"
          onClick={onToggleInfoPanel}
        />

        {/* ── Ansicht: Tag/Woche/Monat/Jahr via Sheet ── */}
        <BarSlot
          label="Ansicht"
          icon={<LayoutGrid className="h-5 w-5" />}
          ariaLabel={`Ansicht wählen — aktuell ${VIEW_LABEL[view]}`}
          ariaHasPopup
          onClick={() => setViewSheet(true)}
        />

        {/* ── Admin/Manager: View-As ── */}
        {showViewAsPicker && (
          <BarSlot
            label="Mitarbeiter"
            icon={<User className="h-5 w-5" />}
            active={viewAsUserId !== ''}
            ariaLabel="Als Mitarbeiter anzeigen"
            ariaHasPopup
            onClick={() => setViewAsSheet(true)}
          />
        )}

        {/* ── Stoppuhr: nur eigene Ansicht; laufend = Ping-Dot + Live-Zeit ── */}
        {showTimer && (
          <BarSlot
            label="Stoppuhr"
            active={timerRunning}
            ariaLabel={timerRunning ? `Timer läuft — ${formatClock(elapsed)}` : 'Stoppuhr'}
            ariaHasPopup
            onClick={() => setTimerOpen(true)}
            icon={
              timerRunning ? (
                <span className="flex items-center gap-1.5">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
                  </span>
                  <span className="font-mono text-xs leading-none tabular-nums">
                    {formatClock(elapsed)}
                  </span>
                </span>
              ) : (
                <TimerIcon className="h-5 w-5" />
              )
            }
          />
        )}

        {/* ── ＋ Erfassen: Primäraktion, sticht als Accent-Kreis heraus.
            Im Lesemodus deaktiviert statt versteckt — die Bar bleibt so
            layout-stabil beim View-As-Wechsel. ── */}
        <BarAccentPlus
          ariaLabel="Erfassen"
          onClick={onErfassen}
          disabled={readOnly}
          title={readOnly ? 'Im Lesemodus deaktiviert' : 'Erfassen'}
        />
      </MobileBottomSlots>

      {/* ── View-Sheet: nur Tag/Woche/Monat/Jahr — „Liste" ist eigener
          Bar-Slot. ── */}
      <SidePanel open={viewSheet} onClose={() => setViewSheet(false)} title="Ansicht">
        <div className="flex flex-col gap-1">
          {VIEW_ORDER.map((v) => (
            <SheetRow
              key={v}
              active={view === v}
              label={VIEW_LABEL[v]}
              onClick={() => {
                onViewChange(v)
                setViewSheet(false)
              }}
            />
          ))}
        </div>
      </SidePanel>

      {/* ── View-As-Sheet (nur Admin/Manager) ── */}
      {showViewAsPicker && (
        <SidePanel
          open={viewAsSheet}
          onClose={() => setViewAsSheet(false)}
          title="Als Mitarbeiter anzeigen"
        >
          <div className="flex flex-col gap-1">
            <SheetRow
              active={viewAsUserId === ''}
              label="Eigene Ansicht"
              onClick={() => {
                onViewAsChange('')
                setViewAsSheet(false)
              }}
            />
            {manageableUsers
              .filter((u) => u.id !== selfUserId)
              .map((u) => (
                <SheetRow
                  key={u.id}
                  active={viewAsUserId === u.id}
                  label={u.name}
                  onClick={() => {
                    onViewAsChange(u.id)
                    setViewAsSheet(false)
                  }}
                />
              ))}
          </div>
        </SidePanel>
      )}

      {/* Timer-Sheet: sheet erzwingt das Portal-Bottom-Sheet (z-50, deckelt
          auch die Bar) — auch im Tablet-Band, wo isMobile false ist: der
          Desktop-Zweig des Popovers ist ein Anker-Popover (absolute
          top-full) und hätte hier keinen Anker. */}
      {timerOpen && (
        <CalendarTimerPopover
          sheet
          running={running}
          elapsed={elapsed}
          onChanged={onChanged}
          onClose={() => setTimerOpen(false)}
        />
      )}
    </>
  )
}
